import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles,
  ArrowLeft,
  ArrowRight,
  Loader2,
  LayoutTemplate,
  Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { templatesApi, sectorsApi, messageTypesApi } from '../lib/api';
import { useCredits } from '../context/CreditsContext';

const DESIGN_PATTERNS = [
  { id: 'moderno', name: 'Moderno', description: 'Diseño limpio y actual' },
  { id: 'minimal', name: 'Minimalista', description: 'Simple y elegante' },
  { id: 'bold', name: 'Impactante', description: 'Colores vibrantes y CTAs destacados' },
  { id: 'corporate', name: 'Corporativo', description: 'Profesional y formal' },
  { id: 'creative', name: 'Creativo', description: 'Original y llamativo' },
];

const Step1 = ({ data, setData, sectors, messageTypes }) => (
  <div className="space-y-6">
    <div>
      <Label htmlFor="sector">Sector / Industria</Label>
      <Select value={data.sector} onValueChange={(v) => setData({ ...data, sector: v })}>
        <SelectTrigger id="sector" className="mt-2" data-testid="select-sector">
          <SelectValue placeholder="Selecciona un sector" />
        </SelectTrigger>
        <SelectContent>
          {sectors.map((sector) => (
            <SelectItem key={sector.id} value={sector.id}>
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: sector.colors?.primary || '#4F46E5' }}
                />
                {sector.name}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-sm text-slate-500 mt-1">
        El sector determina el tono y estilo del contenido
      </p>
    </div>

    <div>
      <Label htmlFor="messageType">Tipo de mensaje</Label>
      <Select value={data.messageType} onValueChange={(v) => setData({ ...data, messageType: v })}>
        <SelectTrigger id="messageType" className="mt-2" data-testid="select-message-type">
          <SelectValue placeholder="Selecciona el tipo" />
        </SelectTrigger>
        <SelectContent>
          {messageTypes.map((type) => (
            <SelectItem key={type.id} value={type.id}>
              {type.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-sm text-slate-500 mt-1">
        Define la estructura y propósito del email
      </p>
    </div>
  </div>
);

const Step2 = ({ data, setData }) => (
  <div className="space-y-6">
    <div>
      <Label>Patrón de diseño</Label>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
        {DESIGN_PATTERNS.map((pattern) => (
          <button
            key={pattern.id}
            onClick={() => setData({ ...data, designPattern: pattern.id })}
            className={`p-4 rounded-lg border-2 text-left transition-all ${
              data.designPattern === pattern.id
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-slate-200 hover:border-slate-300'
            }`}
            data-testid={`pattern-${pattern.id}`}
          >
            <p className="font-medium text-slate-900">{pattern.name}</p>
            <p className="text-xs text-slate-500 mt-1">{pattern.description}</p>
            {data.designPattern === pattern.id && (
              <Check className="w-5 h-5 text-indigo-500 mt-2" />
            )}
          </button>
        ))}
      </div>
    </div>
  </div>
);

const Step3 = ({ data, setData }) => (
  <div className="space-y-6">
    <div>
      <Label htmlFor="customInstructions">Instrucciones personalizadas (opcional)</Label>
      <Textarea
        id="customInstructions"
        placeholder="Ej: Necesito una plantilla con 3 secciones, cada sección con una foto del lado izquierdo y una tabla de precios del lado derecho..."
        value={data.customInstructions}
        onChange={(e) => setData({ ...data, customInstructions: e.target.value })}
        className="mt-2 min-h-[150px]"
        data-testid="custom-instructions"
      />
      <p className="text-sm text-slate-500 mt-1">
        Describe cómo quieres que sea tu plantilla. Puedes especificar el número de secciones, tipos de bloques, disposición, etc.
      </p>
    </div>

    <Card className="bg-slate-50 border-slate-200">
      <CardContent className="p-4">
        <h4 className="font-medium text-slate-900 mb-2">Resumen</h4>
        <ul className="text-sm text-slate-600 space-y-1">
          <li><strong>Sector:</strong> {data.sector || '-'}</li>
          <li><strong>Tipo:</strong> {data.messageType || '-'}</li>
          <li><strong>Diseño:</strong> {data.designPattern || '-'}</li>
          <li><strong>Costo:</strong> 5 créditos</li>
        </ul>
      </CardContent>
    </Card>
  </div>
);

export const TemplateGenerator = () => {
  const navigate = useNavigate();
  const { credits, refreshCredits } = useCredits();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sectors, setSectors] = useState([]);
  const [messageTypes, setMessageTypes] = useState([]);
  const [data, setData] = useState({
    sector: '',
    messageType: '',
    designPattern: 'moderno',
    customInstructions: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sectorsRes, typesRes] = await Promise.all([
          sectorsApi.list(),
          messageTypesApi.list(),
        ]);
        setSectors(sectorsRes.data.sectors);
        setMessageTypes(typesRes.data.messageTypes);
      } catch (error) {
        console.error('Error:', error);
      }
    };
    fetchData();
  }, []);

  const canProceed = () => {
    if (step === 1) return data.sector && data.messageType;
    if (step === 2) return data.designPattern;
    return true;
  };

  const handleGenerate = async () => {
    if (credits.balance < 5) {
      toast.error('Créditos insuficientes. Necesitas 5 créditos.');
      return;
    }

    setLoading(true);
    try {
      const response = await templatesApi.generate({
        messageType: data.messageType,
        designPattern: data.designPattern,
        sector: data.sector,
        customInstructions: data.customInstructions || null,
      });

      refreshCredits();
      toast.success('Plantilla generada correctamente');
      
      // Navigate to campaign builder with the new template
      navigate(`/campaigns/new`, { 
        state: { generatedTemplate: response.data.template }
      });
    } catch (error) {
      console.error('Error generating template:', error);
      toast.error(error.response?.data?.detail || 'Error al generar la plantilla');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, title: 'Contexto' },
    { num: 2, title: 'Diseño' },
    { num: 3, title: 'Detalles' },
  ];

  return (
    <div className="p-8 animate-fade-in" data-testid="template-generator-page">
      {/* Header */}
      <div className="mb-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/templates')}
          className="mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver a plantillas
        </Button>
        <h1 className="text-3xl font-bold font-[Manrope] text-slate-900">Generar Plantilla con IA</h1>
        <p className="text-slate-500 mt-1">Crea una plantilla personalizada describiendo lo que necesitas</p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center mb-8">
        {steps.map((s, i) => (
          <React.Fragment key={s.num}>
            <div className="flex items-center">
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                  step >= s.num 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-slate-100 text-slate-400'
                }`}
              >
                {step > s.num ? <Check className="w-5 h-5" /> : s.num}
              </div>
              <span className={`ml-2 font-medium ${step >= s.num ? 'text-slate-900' : 'text-slate-400'}`}>
                {s.title}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`w-16 h-1 mx-4 rounded ${step > s.num ? 'bg-indigo-600' : 'bg-slate-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Form */}
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-600" />
            {steps[step - 1].title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {step === 1 && <Step1 data={data} setData={setData} sectors={sectors} messageTypes={messageTypes} />}
          {step === 2 && <Step2 data={data} setData={setData} />}
          {step === 3 && <Step3 data={data} setData={setData} />}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t">
            <Button
              variant="ghost"
              onClick={() => setStep(step - 1)}
              disabled={step === 1}
              data-testid="prev-step"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Anterior
            </Button>

            {step < 3 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                data-testid="next-step"
              >
                Siguiente
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleGenerate}
                disabled={loading || credits.balance < 5}
                className="ai-button text-white"
                data-testid="generate-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generar (5 créditos)
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TemplateGenerator;
